import {
    ActionButton,
    Badge,
    Dialog,
    DialogContent,
} from "@jhool-io/fe-components";
import { isToday } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useIsMutating } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { onValue, query, ref, off } from "firebase/database";
import {
    cn,
    displayNameInRightFormat,
    formatDate,
} from "../../../../../../utils/helpers";
import CancelSessionForm from "../../../../../schedule/components/ClinicianSchedule/CaseloadView/CancelSessionForm/CancelSessionForm";
import EditSessionTimeForm from "../../../../../schedule/components/ClinicianSchedule/CaseloadView/EditSessionTimeForm/EditSessionTimeForm";
import {
    ITelehealthCallParticipants,
    RecurrenceRule,
} from "../../../../../../utils/types";
import { IProviderUpcomingSession } from "../../../../../providers/types/providers.types";
import { rtdb } from "../../../../../../firebase";
import RefreshIcon from "../../../../../../icons/Refresh";
import TimesOctagonIcon from "../../../../../../icons/TimesOctagon";
import VideoIcon from "../../../../../../icons/Video";
import { IWeeklySchedule } from "../../../../../schedule/types/schedule.types";

interface UpcomingAppointmentProps {
    appointment: IProviderUpcomingSession | IWeeklySchedule;
}

type Dialogs = "cancel_session" | "reschedule_session";

export default function UpcomingAppointment({
    appointment,
}: UpcomingAppointmentProps) {
    const [dialogInView, setDialogInView] = useState<Dialogs | null>(null);
    const [shouldNavigate, setShouldNavigate] = useState(false);
    const [participants, setParticipants] = useState<
        ITelehealthCallParticipants[]
    >([]);

    const isCancelSessionMutating = useIsMutating(["cancel-session"]);
    const isEditTimeMutating = useIsMutating(["edit-session-time"]);

    const isClientOnCall = useMemo(
        () =>
            participants
                .filter((participant) => participant.status === "active")
                .some((participant) => participant.role === "client"),
        [participants]
    );

    const dateOfSession =
        "confirmed_start_date_time" in appointment
            ? appointment.confirmed_start_date_time
            : appointment.start_date_time;

    const endDateOfSession =
        "confirmed_end_date_time" in appointment
            ? appointment.confirmed_end_date_time
            : appointment.end_date_time;

    const isDateToday = dateOfSession
        ? isToday(new Date(dateOfSession))
        : false;

    const clientOnCallDetails = useMemo(() => {
        return participants?.find(
            (participant) => participant.role === "client"
        );
    }, [participants]);

    const clientLeftCallText = useMemo(
        () =>
            isDateToday &&
            clientOnCallDetails &&
            clientOnCallDetails.status === "inactive" &&
            isToday(new Date(clientOnCallDetails?.updated_at)) ? (
                <div className="text-dark text-caption italic flex items-center gap-x-[2px] font-medium">
                    <span className="size-2 rounded-full bg-danger blink" />
                    Client left call at
                    {` ${formatDate(
                        clientOnCallDetails.updated_at,
                        false,
                        "hh:mmaa"
                    )}`}
                </div>
            ) : null,
        [clientOnCallDetails, isDateToday]
    );

    const appointmentDateToDisplay = isDateToday
        ? `Today. ${formatDate(dateOfSession || "", false, "hh:mmaa")}`
        : formatDate(dateOfSession || "", false, "hh:mmaa. eee. MM/dd");

    const getAppointmentLocationInfoToShow = () => {
        const deliveryOption =
            "therapy_location" in appointment
                ? appointment.therapy_location
                : appointment.delivery_option;

        if (
            appointment.is_paid &&
            appointment.session_history_id &&
            "start_date_time" in appointment
        ) {
            return (
                <Link
                    to={`/meeting/${
                        appointment.session_history_id
                    }/join?with=${`${appointment.client.first_name}_${appointment.client.last_name}`}`}
                    className="px-2 min-h-[1.375rem] inline-flex items-center justify-center appearance-none font-manrope text-caption focus-visible:[outline:2px_solid_var(--color-interactive-focus)] bg-primary text-white hover:brightness-90 disabled:hover:bg-primary rounded-md gap-x-1"
                >
                    <VideoIcon />
                    Join now
                </Link>
            );
        }
        if (deliveryOption === "remote") {
            return <Badge content="Telemed" type="neutral" />;
        }
        return <Badge content="In person" type="neutral" />;
    };

    const getCardBgColor = () => {
        switch (appointment.recurrence_rule) {
            case RecurrenceRule.MONTHLY:
                return "var(--bg-appointment-monthly)";

            case RecurrenceRule.WEEKLY:
                return "var(--bg-appointment-weekly)";

            case RecurrenceRule.BIWEEKLY:
                return "var(--bg-appointment-bi_weekly)";

            case RecurrenceRule.ONE_OFF:
                return "var(--bg-appointment-one_off)";

            case RecurrenceRule.TRIWEEKLY:
                return "var(--bg-appointment-tri_weekly)";

            case RecurrenceRule.TWO_MONTHS:
                return "var(--bg-appointment-two_months)";

            case RecurrenceRule.THREE_MONTHS:
                return "var(--bg-appointment-three_months)";

            default:
                return "var(--bg-appointment-three_months)";
        }
    };

    useEffect(() => {
        const callRef = query(
            ref(rtdb, `/telehealth_calls/${appointment.session_history_id}`)
        );

        const unsubscribe = onValue(callRef, (snapShot) => {
            const callParticipants = snapShot.val();

            if (callParticipants) {
                const keys = Object.keys(callParticipants);
                let partcipantsArray: ITelehealthCallParticipants[] = [];

                keys.forEach((p) => {
                    partcipantsArray = [
                        {
                            ...callParticipants[p],
                        },
                        ...partcipantsArray,
                    ];
                });

                setParticipants(partcipantsArray);
            }
        });

        return () => {
            off(callRef, "value", unsubscribe);
        };
    }, [appointment.session_history_id]);

    return (
        <>
            <Dialog open={dialogInView === "cancel_session"}>
                <DialogContent
                    title="Cancel session"
                    variant="center"
                    onSaveClick={() => setShouldNavigate(true)}
                    handleCloseDialog={() => setDialogInView(null)}
                    isCancelBtnDisabled={isCancelSessionMutating > 0}
                    isSubmitBtnDisabled={isCancelSessionMutating > 0}
                    onCancelClick={() => setShouldNavigate(false)}
                    cancelText="Yes,cancel"
                    saveText="No,create No show note"
                    canCancelButtonSubmit
                    cancelBtnFormValue="cancel_client_session"
                    submitBtnFormValue="cancel_client_session"
                >
                    <CancelSessionForm
                        onFormSubmit={() => setDialogInView(null)}
                        shouldNavigate={shouldNavigate}
                        sessionHistoryId={
                            appointment.session_history_id || null
                        }
                        action="cancel"
                    />
                </DialogContent>
            </Dialog>
            {appointment.session_history_id &&
            dateOfSession &&
            endDateOfSession ? (
                <Dialog open={dialogInView === "reschedule_session"}>
                    <DialogContent
                        title="Edit Session Time"
                        variant="center"
                        handleCloseDialog={() => setDialogInView(null)}
                        isCancelBtnDisabled={isEditTimeMutating > 0}
                        isSubmitBtnDisabled={isEditTimeMutating > 0}
                        showFooter
                        submitBtnFormValue="edit-session-time"
                    >
                        <EditSessionTimeForm
                            sessionHistoryId={appointment.session_history_id}
                            sessionStartDateTime={dateOfSession}
                            sessionEndDateTime={endDateOfSession}
                            onFormSubmit={() => {
                                setDialogInView(null);
                            }}
                        />
                    </DialogContent>
                </Dialog>
            ) : null}
            <div
                className={cn(
                    `rounded-md relative group flex flex-col gap-y-3 border-none py-2 bg-[${getCardBgColor()}] px-[--padding-card-x-lg] py-[--padding-card-y-lg]`
                )}
                style={{
                    backgroundColor: getCardBgColor(),
                }}
            >
                <div className="flex flex-col min-[500px]:flex-row items-start min-[500px]:items-center justify-between gap-[--spacing-items] flex-wrap">
                    <span className="text-base font-medium text-dark tracking-[-0.084px]">
                        {displayNameInRightFormat({
                            firstName: appointment.client.first_name,
                            lastName: appointment.client.last_name,
                        })}
                    </span>
                    <div className="flex items-center gap-x-2 gap-y-[--spacing-items] flex-wrap">
                        <span className="text-dark opacity-55 font-normal text-caption">
                            {appointmentDateToDisplay}
                        </span>
                        {appointment.is_paid ? (
                            <Badge content="Paid" type="success" />
                        ) : (
                            <Badge content="Unpaid" type="danger" />
                        )}
                        {getAppointmentLocationInfoToShow()}
                    </div>
                </div>
                <div className="flex items-center justify-between gap-x-2">
                    <div className="px-2 py-1 min-h-7 flex items-center gap-x-1.5 rounded-md bg-white [border] shadow-[0px_6px_10px_0px_rgba(14,18,27,0.06),_0px_2px_4px_0px_rgba(14,18,27,0.03)] flex-wrap gap-y-2">
                        {appointment.session_history_id &&
                        appointment.start_date_time &&
                        appointment.end_date_time ? (
                            <ActionButton
                                className="px-6 h-6 hover:bg-[#e7e8ec]"
                                size="auto"
                                variant="normal"
                                onClick={() =>
                                    setDialogInView("reschedule_session")
                                }
                                actionTitle="Reschedule session"
                            >
                                <RefreshIcon className="size-3.5 stroke-dark" />
                            </ActionButton>
                        ) : null}

                        <ActionButton
                            className="px-6 h-6 hover:bg-[#e7e8ec]"
                            size="auto"
                            variant="normal"
                            onClick={() => setDialogInView("cancel_session")}
                            actionTitle="Cancel session"
                        >
                            <TimesOctagonIcon className="size-3.5 stroke-dark" />
                        </ActionButton>
                    </div>
                    {isClientOnCall ? (
                        <div className="text-dark text-caption italic flex items-center gap-x-[2px] font-medium">
                            <span className="size-2 rounded-full bg-primary blink" />
                            Client has joined the call
                        </div>
                    ) : (
                        clientLeftCallText
                    )}
                </div>
            </div>
        </>
    );
}
